import styled from '@emotion/styled';
import { type ObjectId, ObjectIdSchema } from '@racemap/sdk/schema/base';
import type { TrackerPrototype } from '@racemap/sdk/schema/trackers';
import TestSelectors from '@racemap/utilities/consts/test_selectors';
import { isEmptyString } from '@racemap/utilities/functions/utils';
import React, { type FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useIsAdmin } from 'src/lib/customHooks';
import { Tristate } from '../../ButtonWithSpinner';
import { CSVDropZone } from '../../DropZone/CSVDropZone';
import { IconFail, IconSpinner } from '../../Icon';
import Modal from '../../Modal';
import { AddTrackerForm, getTrackerPrototype } from './CreateTrackerForm';
import { useTrackerParams } from './useTrackerParams';

interface Props {
  triggerElement: React.ReactElement<{ onClick: () => void }>;
  onCreateNewTracker: (newTrackerPrototypes: Array<TrackerPrototype>) => Promise<void>;
  setIsAddTrackersModalOpen: (isAddTrackersModalOpen: boolean) => void;
  setTrackersToAdd: (trackersToAdd: Array<TrackerPrototype>) => void;
}

export const CreateTrackersModal: FC<Props> = ({
  triggerElement,
  onCreateNewTracker,
  setIsAddTrackersModalOpen,
  setTrackersToAdd,
}) => {
  const { deviceType, appId, deviceName } = useTrackerParams();

  const [newTracker, setNewTracker] = useState(() =>
    getTrackerPrototype(deviceType, appId, deviceName),
  );

  // // If an appId can be extracted from the location state, it indicates a predefined tracker.
  const isPredefinedTracker = appId != null;

  useEffect(() => {
    setNewTracker(getTrackerPrototype(deviceType, appId, deviceName));
  }, [deviceType, appId, deviceName]);

  const [buttonState, setButtonState] = useState(Tristate.NORMAL);
  const [show, setShow] = useState(false);
  const selectors = TestSelectors.admin.trackers.addTrackerModal;
  const isLoading = buttonState === Tristate.SPINNING;
  const isError = buttonState === Tristate.FAILURE;
  const navigate = useNavigate();
  const isAdmin = useIsAdmin();

  const handleCreateNewTracker = async () => {
    const { trackerId, trackerType } = newTracker;
    if (trackerId == null || trackerType == null) return;

    try {
      setButtonState(Tristate.SPINNING);
      await onCreateNewTracker([newTracker]);
      setNewTracker(getTrackerPrototype(trackerType));
      setButtonState(Tristate.NORMAL);
    } catch (err) {
      console.error(err);
      setButtonState(Tristate.FAILURE);
    }
  };

  const handleCSVDrop = async (trackers: Array<TrackerPrototype>) => {
    try {
      const trackersWithICCID = trackers.filter((tracker) => tracker.iccid !== undefined);
      if (!isAdmin && trackersWithICCID.length > 0) {
        const trackerNames = trackersWithICCID.map((tracker) => tracker.trackerName);
        const errorMessage = `Only admins can add trackers with ICCID. Problematic trackers: ${trackerNames.join(', ')}`;
        toast.error(errorMessage);
        throw new Error(errorMessage);
      }
      setButtonState(Tristate.SPINNING);
      setTrackersToAdd(trackers);
      handleCloseModal();
      setIsAddTrackersModalOpen(true);
      setButtonState(Tristate.NORMAL);
    } catch (err) {
      console.error('Error adding trackers:', err);
      setButtonState(Tristate.FAILURE);
    }
  };

  const actionButtonIcon = isLoading ? <IconSpinner /> : isError ? <IconFail /> : null;
  const actionButtonLabel = <>{actionButtonIcon} Add Tracker</>;

  useEffect(() => {
    if (location.pathname === '/admin/trackers/add') {
      setShow(true);
    } else {
      setShow(false);
    }
  }, [location.pathname]);

  const handleCloseModal = () => {
    setShow(false);
    navigate('/admin/trackers');
  };

  return (
    <>
      {React.cloneElement(triggerElement, { onClick: () => setShow(true) })}
      <Modal
        title="Add Tracker:"
        withFooter
        actionButtonLabel={actionButtonLabel}
        onAction={handleCreateNewTracker}
        actionButtonId={selectors.addTrackerButton.toString()}
        actionButtonDisabled={isLoading}
        cancelButtonDisabled={isLoading}
        show={show}
        onShow={() => setShow(true)}
        onClose={handleCloseModal}
        standalone
        size="lg"
      >
        <AddTrackerForm
          value={newTracker}
          onChange={setNewTracker}
          isPredefinedTracker={isPredefinedTracker}
        />
        <hr />
        <InfoText>
          You can add several trackers simultaneously to your management with a csv file. Use our{' '}
          <a
            href={
              'https://static-cdn.racemap.com/importSamples/tracker-management-upload-minimal.csv'
            }
            target="_blank"
            rel="noreferrer"
          >
            sample file
          </a>{' '}
          , modify it and drop that on the zone. By uploading you confirm the pay per use
          subscription:
          <ul style={{ marginTop: '10px' }}>
            <li>
              1€ per <strong>chargable</strong> device for 30 days
            </li>
            <li>No minimum running time</li>
            <li>Free usage for 5 GPS trackers</li>
          </ul>
          How does a tracker become chargeable:
          <ul style={{ marginTop: '10px' }}>
            <li>Only devices that are inserted into the tracker management.</li>
            <li>Devices become chargeable with usage, automatically.</li>
            <li>
              Set the new archive status for specific devices to prevent unintentional charges.
              Archived devices work for live tracking, nevertheless.
            </li>
          </ul>
        </InfoText>
        {!isPredefinedTracker && (
          <CSVDropZone<TrackerPrototype>
            showOverlayAlways
            onCSVDrop={handleCSVDrop}
            transformHeader={transformHeader}
            transform={transformValue}
            withFileDialog
          >
            <TrackerDropZone />
          </CSVDropZone>
        )}
      </Modal>
    </>
  );
};

const InfoText = styled.div`
  padding-bottom: 10px;
  font-weight: 400;
`;

const TrackerDropZone = styled.div`
  height: 150px;
`;

const headerMapping: Record<string, string> = {
  id: 'id',
  tracker_id: 'trackerId',
  imei: 'trackerId',
  tracker_name: 'trackerName',
  name: 'trackerName',
  device_type: 'trackerType',
  iccid: 'iccid',
};
const headers = new Set(Object.values(headerMapping));

const transformHeader = (header: string): string => {
  return headerMapping[header] || header;
};

const transformValue = (value: string, header: string): string | ObjectId | undefined => {
  try {
    if (isEmptyString(value) || !headers.has(header)) return undefined;
    if (header === 'id') return ObjectIdSchema.parse(value);
    return value;
  } catch {
    return undefined;
  }
};
